// The Mortgage Man brand constants, shared by every TMM-only shell surface.
// Source of truth: Mortgage-Man-Command-Center/brand/brand.json (grounded in
// the live themortgageman.co.nz site). The visual language is the kickoff
// deck's: white-first, vivid red bands with torn/angled edges, Ubuntu Light
// (300) type with Medium (500) buttons at 5px radius. Green #4AD504 is
// logo-artwork-only and must never appear as a UI colour.
import * as instanceModule from '@shell-active-instance'

const TMM_BASE = {
  red: '#FB0404',
  redDark: '#B00303',
  redDeep: '#8F0202',
  redWash: '#FDEAEA',
  ink: '#1C1C1C',
  grey: '#383838',
  body: '#3E3E3E',
  card: '#F6F6F4',
  paper: '#FFFFFF',
  border: '#E6E6E2',
  radius: '5px',
  gradient: 'linear-gradient(135deg, #B00303 0%, #FB0404 60%)',
  /** Torn/angled section edges — the site + gallery band treatment. */
  tornBottom: 'polygon(0 0, 100% 0, 100% calc(100% - 26px), 0 100%)',
  tornTop: 'polygon(0 26px, 100% 0, 100% 100%, 0 100%)',
  logo: '/branding/mortgage-man-logo.png',
  font: "'Ubuntu', system-ui, sans-serif",
  /** The face alone, for canvas text (the carousel slides). */
  fontFace: 'Ubuntu',
  /** Small capitals on the brand band. */
  bandMuted: '#FFDBDB',
  /** A light line in the accent's family. */
  redLine: '#F7C8C8',
  /** A border for the accent-wash chips. */
  redBorder: '#F5B5B5',
}

/**
 * A prospect demo build folds the client's own values away (no trace of them
 * ships): plain values under the prospect's `tmm`, which sets every token.
 */
const PLAIN: typeof TMM_BASE = {
  red: '#2563EB',
  redDark: '#1D4ED8',
  redDeep: '#1E3A8A',
  redWash: '#EFF6FF',
  ink: '#1C1C1C',
  grey: '#4B5563',
  body: '#4B5563',
  card: '#F6F6F4',
  paper: '#FFFFFF',
  border: '#E5E7EB',
  radius: '8px',
  gradient: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)',
  tornBottom: 'none',
  tornTop: 'none',
  logo: '',
  font: 'system-ui, sans-serif',
  fontFace: 'system-ui',
  bandMuted: '#DBEAFE',
  redLine: '#BFDBFE',
  redBorder: '#BFDBFE',
}
const BASE = __PROSPECT_DEMO__ ? PLAIN : TMM_BASE

/**
 * The brand tokens. A prospect demo on these pages (src/prospects/surface-apps.mortgage.ts)
 * exports `tmm` from its instance.ts with its own values, laid over these, so
 * the pages wear its look. Every other build exports none and keeps these.
 */
export const TMM: typeof BASE = {
  ...BASE,
  ...((instanceModule as Record<string, unknown>).tmm as Partial<typeof BASE> | undefined),
}

/** Shared kicker/eyebrow style (deck `.kicker`). */
export const tmmKicker = {
  margin: 0,
  textTransform: 'uppercase',
  letterSpacing: '.14em',
  fontSize: 11,
  fontWeight: 700,
  color: TMM.redDark,
} as const

/**
 * Fluid page container — fills the shell's main column edge to edge, with
 * side breathing room that scales with the viewport instead of a fixed
 * 1060px centred column (Sam, 3 Sep 2026: "why are we not filling the
 * space?"). No max-width on purpose: the surfaces grow with the monitor;
 * prose keeps its own measure via per-paragraph maxWidth.
 */
export const tmmContainer = {
  width: '100%',
  // Padding sits inside the 100% width; without this the right column ran off-screen.
  boxSizing: 'border-box',
  margin: '0 auto',
  paddingLeft: 'clamp(1.5rem, 4vw, 4.5rem)',
  paddingRight: 'clamp(1.5rem, 4vw, 4.5rem)',
} as const

/** A brand colour (#RRGGBB) at some opacity, as rgba(): for canvas and gradients. */
export function tmmAlpha(hex: string, a: number): string {
  const n = parseInt(hex.replace('#', '').slice(0, 6), 16)
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`
}
